import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { inr, chipGlow, ENQUIRY_STATUS } from "@/lib/format";
import { ArrowLeft, MessageCircle, Mail, Send, Loader2, Phone, Save } from "lucide-react";
import { toast } from "sonner";

export default function EnquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [enq, setEnq] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [notes, setNotes] = useState("");
  const [followUp, setFollowUp] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/enquiries/${id}`);
      setEnq(data); setNotes(data.notes || ""); setFollowUp(data.follow_up_date || "");
    } catch (e) { toast.error(apiError(e)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!enq)
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#2997ff]" /></div>;

  const setStatus = async (status) => {
    try { const { data } = await api.patch(`/enquiries/${id}`, { status }); setEnq({ ...enq, status: data.status }); toast.success("Updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveNotes = async () => {
    try { await api.patch(`/enquiries/${id}`, { notes, follow_up_date: followUp }); toast.success("Saved"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/enquiries/${id}/reply`, { message: reply });
      setEnq(data); setReply(""); toast.success("Reply emailed to customer");
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const waUrl = `https://wa.me/${(enq.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${enq.name}, this is MacFreaks regarding your enquiry.`)}`;
  const u = enq.linked_unit;
  const input = "w-full rounded-xl border border-white/10 bg-black px-3 py-2 text-sm outline-none focus:border-[#2997ff]";

  return (
    <div className="mf-fade-in pb-4">
      <button onClick={() => navigate(-1)} className="mb-3 flex items-center gap-1 text-sm text-[#98989d]" data-testid="back-button">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="rounded-3xl border border-white/10 bg-[#121214] p-5">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight">{enq.name}</h1>
            <div className="mt-1 flex items-center gap-1 text-sm text-[#98989d]"><Phone size={13} /> {enq.phone}</div>
            {enq.email && <div className="flex items-center gap-1 text-sm text-[#98989d]"><Mail size={13} /> {enq.email}</div>}
          </div>
          <span className="rounded-full bg-[#2997ff]/15 px-3 py-1 text-xs font-semibold text-[#2997ff]">{enq.type}</span>
        </div>
        {enq.message && <p className="mt-3 whitespace-pre-wrap rounded-xl border border-white/5 bg-black/40 p-3 text-sm text-[#e5e5e7]">{enq.message}</p>}
        {enq.source && <p className="mt-2 text-xs text-[#98989d]">Source: {enq.source}</p>}

        <div className="mt-3 flex gap-2">
          <a data-testid="enquiry-whatsapp-button" href={waUrl} target="_blank" rel="noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#30d158]/40 bg-[#30d158]/10 py-2.5 text-sm font-semibold text-[#30d158] active:scale-[0.98]">
            <MessageCircle size={15} /> WhatsApp
          </a>
        </div>
      </div>

      {/* linked unit */}
      {u && (
        <button onClick={() => navigate(`/unit/${u.id}`)} data-testid="linked-unit-card" className="mt-3 flex w-full items-center gap-3 overflow-hidden rounded-2xl border border-white/10 bg-[#121214] p-3 text-left active:scale-[0.99]">
          <div className="h-12 w-12 shrink-0 rounded-lg" style={{ background: `${chipGlow(u.chip)}33`, border: `1px solid ${chipGlow(u.chip)}` }} />
          <div className="min-w-0 flex-1">
            <div className="font-mono text-xs text-[#98989d]">{u.stock_code} · {u.status}</div>
            <div className="truncate text-sm font-semibold">{u.size} {u.chip} · {u.memory}/{u.storage}</div>
          </div>
          <div className="text-sm font-bold">{u.price ? inr(u.price) : "—"}</div>
        </button>
      )}

      {/* status */}
      <div className="mt-3 rounded-2xl border border-white/10 bg-[#121214] p-4">
        <div className="mb-2 text-xs font-medium text-[#98989d]">Status</div>
        <div className="flex flex-wrap gap-2">
          {ENQUIRY_STATUS.map((s) => (
            <button key={s} data-testid={`status-${s}`} onClick={() => setStatus(s)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${enq.status === s ? "bg-[#2997ff] text-white" : "border border-white/10 text-[#98989d]"}`}>{s}</button>
          ))}
        </div>
      </div>

      {/* notes + follow up */}
      <div className="mt-3 rounded-2xl border border-white/10 bg-[#121214] p-4">
        <label className="mb-1 block text-xs font-medium text-[#98989d]">Notes</label>
        <textarea data-testid="enquiry-notes" className={`${input} min-h-16`} value={notes} onChange={(e) => setNotes(e.target.value)} />
        <label className="mb-1 mt-3 block text-xs font-medium text-[#98989d]">Follow-up date</label>
        <input data-testid="enquiry-followup-date" type="date" className={input} value={followUp || ""} onChange={(e) => setFollowUp(e.target.value)} />
        <button data-testid="save-notes-button" onClick={saveNotes} className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-[#161618] py-2.5 text-sm font-semibold active:scale-[0.98] w-full">
          <Save size={15} /> Save notes & follow-up
        </button>
      </div>

      {/* reply thread */}
      <div className="mt-3 rounded-2xl border border-white/10 bg-[#121214] p-4">
        <div className="mb-2 text-sm font-semibold">Reply by email</div>
        {(enq.replies || []).map((r, i) => (
          <div key={i} className="mb-2 rounded-xl border border-white/5 bg-black/40 p-3 text-sm">
            <div className="mb-1 text-[10px] text-[#98989d]">{r.from} · {(r.sent_at || "").slice(0, 16).replace("T", " ")}</div>
            <div className="whitespace-pre-wrap text-[#e5e5e7]">{r.message}</div>
          </div>
        ))}
        {!enq.email && <p className="mb-2 text-xs text-[#ff9f0a]">No email on file — use WhatsApp instead.</p>}
        <textarea data-testid="reply-textarea" disabled={!enq.email} className={`${input} min-h-20`} placeholder="Type your reply to the customer…" value={reply} onChange={(e) => setReply(e.target.value)} />
        <button data-testid="send-reply-button" onClick={sendReply} disabled={sending || !enq.email || !reply.trim()} className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-[#2997ff] py-2.5 text-sm font-semibold active:scale-[0.98] disabled:opacity-50">
          {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={15} />} Send reply
        </button>
      </div>
    </div>
  );
}
